import { Panel } from 'primereact/panel';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton } from '../../../button/button';
import {
  formatDateRange,
  getCustomDateRangeItem,
} from '../../../date-filter-overlay-panel/date-range-filter-functions';
import { isDropDownNoSelection } from '../../../fields/drop-down/drop-down-common';
import type { DropDownNoSelection, DropDownResult } from '../../../fields/drop-down/drop-down-types';
import { OdinIconSize } from '../../../odin-icon/odin-icon.component';
import FilterChip from '../common/filter-chip.component';
import {
  transformDataGridColumnSearchValueToDateRange,
  transformDateRangeToContinuousScrollerSearchValueFilter,
} from '../data-grid-date-filter.component';
import type { MobileFilterInterface } from '../data-grid-filter.interface';
import type { DateRange } from '../date-range-filter-field.component';
import DateRangeFilterField from '../date-range-filter-field.component';

type AppliedFilterType = DateRange & { quickDateRange?: DropDownResult | DropDownNoSelection };

export default function MobileDateFilter({ column, filterValue, onInputChange, id }: MobileFilterInterface) {
  const panelRef = useRef<Panel>(null);
  const showFilterPanel = () => panelRef.current?.toggle(undefined);

  const [quickDateRange, setQuickDateRange] = useState<DropDownResult | DropDownNoSelection>();
  const [fromDate, setFromDate] = useState<Date>();
  const [toDate, setToDate] = useState<Date>();
  const appliedDateRange = useRef<AppliedFilterType | undefined>(undefined);

  useEffect(() => {
    if (filterValue?.value === undefined) {
      clearFilter();
      return;
    }
    const passedValue = filterValue.value as Date | Array<Date>;
    const comparison = filterValue.comparison;

    const value = transformDataGridColumnSearchValueToDateRange(passedValue, comparison);
    updateFromDate(value?.from);
    updateToDate(value?.to);
    let newQuickDateRange: DropDownResult | DropDownNoSelection = getCustomDateRangeItem();
    if (filterValue.customDataProperties?.quickDateRange) {
      newQuickDateRange = filterValue.customDataProperties.quickDateRange as DropDownResult | DropDownNoSelection;
    }

    if (!isDropDownNoSelection(newQuickDateRange) && newQuickDateRange.value !== getCustomDateRangeItem().value) {
      setQuickDateRange(newQuickDateRange);
    } else {
      if (value?.from || value?.to) {
        setQuickDateRange(getCustomDateRangeItem());
      } else {
        setQuickDateRange(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateFromDate and updateToDate are stable useCallback functions with empty deps
  }, [filterValue]);

  const clearFilter = () => {
    setFromDate(undefined);
    setToDate(undefined);
    setQuickDateRange(undefined);
    appliedDateRange.current = undefined;
  };

  const handleClearFilter = () => {
    clearFilter();
    onInputChange({ column, key: id });
  };

  const handleFilterChipClear = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleClearFilter();
  };

  const onFilterApplied = () => {
    const dateRange = appliedDateRange.current ?? {};
    const value = transformDateRangeToContinuousScrollerSearchValueFilter(id, dateRange, column.field);
    const populatedColumn = {
      ...column,
      customDataProperties: { ...column.customDataProperties, quickDateRange: dateRange.quickDateRange },
    };
    onInputChange({ column: populatedColumn, key: id, value: value?.value, comparison: value?.comparison });
  };

  const updateFromDate = useCallback((startDate: Date | undefined) => {
    setFromDate(startDate);
    appliedDateRange.current = { ...appliedDateRange.current, from: startDate };
  }, []);

  const updateToDate = useCallback((endDate: Date | undefined) => {
    setToDate(endDate);
    appliedDateRange.current = { ...appliedDateRange.current, to: endDate };
  }, []);

  const updateQuickDateRange = useCallback((quickRange: DropDownResult | DropDownNoSelection | undefined) => {
    setQuickDateRange(quickRange);
    appliedDateRange.current = { ...appliedDateRange.current, quickDateRange: quickRange };
  }, []);

  const getSelectedRangeText = () => {
    if (!fromDate && !toDate) {
      return '';
    }
    if (
      !quickDateRange ||
      isDropDownNoSelection(quickDateRange) ||
      getCustomDateRangeItem().value === quickDateRange?.value
    ) {
      return formatDateRange(fromDate, toDate);
    } else {
      return quickDateRange?.text;
    }
  };

  return (
    <Panel
      className="odin-flex odin-h-full odin-flex-col odin-w-full odin-cursor-pointer pr-2 odin-border odin-border-gray-4"
      ref={panelRef}
      header={
        <div onClick={showFilterPanel} className="odin-w-full flex flex-row mx-2" role="button">
          <IconButton classNames="odin-bg-transparent odin-pr-1" iconSize={OdinIconSize.ExtraSmall} icon="Calendar" />
          <FilterChip text={getSelectedRangeText()} onClear={handleFilterChipClear} />
        </div>
      }
      collapsed={true}
      toggleable
      data-testid="mobile-date-filter-panel"
    >
      <div className="odin-flex flex-col m-2">
        <DateRangeFilterField
          fromDate={fromDate}
          toDate={toDate}
          quickDateRange={quickDateRange}
          setFromDate={updateFromDate}
          setToDate={updateToDate}
          setQuickDateRange={updateQuickDateRange}
          clearFilter={handleClearFilter}
          onFilterApplied={onFilterApplied}
        />
      </div>
    </Panel>
  );
}
